import React from 'react';

import type { TagColorMode, TagVariant } from '@treneo/shared/constants';
import { TagType } from '@treneo/shared/constants';
import { useTagConfig } from '@treneo/shared/contexts';
import { useAppTranslations } from '@treneo/shared/i18n';
import { AddIcon, ClearIcon, IconSizes, RemoveIcon } from '@treneo/shared/icons';
import { Colors } from '@treneo/shared/themes';
import { styled } from '@treneo/shared/ui';

import { CourseChip, getTagColor } from '../../course-tag';

import type { TagTypeType } from '../types';

const SearchTagChip = styled(CourseChip)<{
  tagType: TagTypeType;
}>`
  position: relative;
  cursor: pointer;
  pointer-events: auto;
  margin: 1px 2px;
  font-size: 11px;
  height: 24px;
  padding: 3px 6px 3px 6px;
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
  white-space: nowrap;

  &:hover {
    opacity: 0.8;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const TagSymbol = styled('span')<{ tagType: TagTypeType; tagColor: string }>`
  margin-right: 2px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background-color: ${({ tagType }) => (tagType === TagType.Include ? Colors.green : Colors.red)};
  border: 1px solid ${({ tagColor }) => tagColor};
  color: ${Colors.white};

  svg {
    font-size: 10px;
    color: ${Colors.white};
  }
`;

const DeleteButton = styled('button')<{ tagColor: string }>`
  background: none;
  border: none;
  color: ${Colors.darkGrey};
  margin-left: 8px;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  min-width: 16px;
  min-height: 16px;

  &:hover {
    color: ${Colors.red};
    opacity: 0.9;
  }

  &:focus {
    outline: 2px solid ${Colors.blue};
    outline-offset: 2px;
    border-radius: 2px;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

type SearchTagProps = Readonly<{
  name: string;
  type: TagTypeType;
  displayName: string;
  onToggle: () => void;
  onRemove: (tagName: string) => void;
  disabled?: boolean;
}>;

export const SearchTag = ({
  name,
  type,
  displayName,
  onToggle,
  onRemove,
  disabled = false
}: SearchTagProps) => {
  const { t } = useAppTranslations();

  const safeName = name || displayName || 'unknown';
  const { tagVariant, tagColorMode, tagStyling } = useTagConfig();
  const tagColor = getTagColor(
    safeName,
    tagVariant as TagVariant,
    tagStyling.uniformColor,
    tagColorMode as TagColorMode
  );

  const handleToggle = () => {
    if (!disabled) {
      onToggle();
    }
  };

  const handleRemove = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!disabled) {
      onRemove(name);
    }
  };

  return (
    <SearchTagChip
      backgroundColor={tagColor}
      variant={tagVariant as TagVariant}
      uniformColor={tagStyling.uniformColor}
      borderSize={tagStyling.borderSize as number | undefined}
      fontSize={tagStyling.fontSize as number | undefined}
      colorMode={tagColorMode as TagColorMode}
      tagType={type}
      onClick={handleToggle}
    >
      <TagSymbol tagType={type} tagColor={tagColor}>
        {type === TagType.Include ? (
          <AddIcon fontSize='inherit' />
        ) : (
          <RemoveIcon fontSize='inherit' />
        )}
      </TagSymbol>
      {displayName}
      <DeleteButton
        onClick={handleRemove}
        aria-label={t('coursesPage.removeTag')}
        disabled={disabled}
        type='button'
        tagColor={tagColor}
      >
        <ClearIcon fontSize={IconSizes.small} />
      </DeleteButton>
    </SearchTagChip>
  );
};
